import React from 'react';
import classNames from 'classnames';
import { NSCard, NSNavTabs } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import { PRO_FORMA_TABS, PRO_FORMA_TABLE_NAV } from 'views/ProFormaTable/constants';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProFormaQuickStartValues } from 'views/public/ProFormaQuickStart/types';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import ProFormaDevelopment from './ProFormaDevelopment/ProFormaDevelopment';
import ProFormaGeneralInformation from './ProFormaGeneralInformation/ProFormaGeneralInformation';
import ProFormaOperations from './ProFormaOperations/ProFormaOperations';
import ProFormaDisposition from './ProFormaDisposition/ProFormaDisposition';
import { useProFormaSocketContext } from './socketContext/ProFormaSocketProvider';
import { checkUsesSourcesUnequal } from '../../helpers';
import './ProForma.scss';
import SignupOverlay from './SignupOverlay/SignupOverlay';

interface IProFormaBaseProps {
    navigateToSection: (sideNavName: string, tabName?: string) => void;
    currentSection?: string;
    currentTab?: string;
    hasWriteAccessToProForma: boolean;
    proFormaSignUpData?: ProFormaQuickStartValues;
    setProFormaSignUpData?: (data: ProFormaQuickStartValues) => void;
}
interface IProFormaSignUpProps extends IProFormaBaseProps {
    signUpMode: true;
    displayManualSignUp: boolean;
    setDisplayManualSignUp: (display: boolean) => void;
    signUpManager: ISignUpManager;
}
interface IProFormaNonSignUpProps extends IProFormaBaseProps {
    signUpMode?: false;
    displayManualSignUp?: boolean;
    setDisplayManualSignUp?: (display: boolean) => void;
    signUpManager?: ISignUpManager;
}

export type IProFormaProps = IProFormaSignUpProps | IProFormaNonSignUpProps;

const ProForma = ({
    navigateToSection,
    currentSection,
    currentTab,
    hasWriteAccessToProForma,
    signUpMode = false,
    proFormaSignUpData,
    setProFormaSignUpData,
    displayManualSignUp,
    setDisplayManualSignUp,
    signUpManager,
}: IProFormaProps) => {
    const { uses, sources, shouldUseProjectActuals } = useProFormaSocketContext();

    return (
        <>
            <NSNavTabs
                containerClassName="px-0"
                navItems={Object.entries(PRO_FORMA_TABS).map(([key, { prefix, name }], index) => ({
                    callback: e => {
                        e.preventDefault();
                        navigateToSection(currentSection || PRO_FORMA_TABLE_NAV.PRO_FORMA.name, name);
                    },
                    navPrefix: prefix,
                    className: classNames('text-nowrap', {
                        'mr-1': index !== Object.keys(PRO_FORMA_TABS).length - 1,
                    }),
                    name,
                    icon: name === PRO_FORMA_TABS.DEVELOPMENT.name
                        && checkUsesSourcesUnequal(uses, sources)
                        && !shouldUseProjectActuals
                        ? (
                            <div className="ProForma__warning-icon d-flex align-items-center justify-content-center">
                                <FontAwesomeIcon icon={faWarning} className="text-warning" />
                            </div>
                        )
                        : undefined,
                    isActive: currentTab === name,
                    id: `proforma-tab-${index}`,
                }))}
            />
            <hr className="my-0" />
            <NSCard className="p-3">
                <RenderIf isTrue={currentTab === PRO_FORMA_TABS.GENERAL_INFORMATION.name}>
                    <ProFormaGeneralInformation
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        signUpMode={signUpMode}
                        proFormaSignUpData={proFormaSignUpData}
                        setProFormaSignUpData={setProFormaSignUpData}
                    />
                </RenderIf>
                <RenderIf isTrue={currentTab === PRO_FORMA_TABS.DEVELOPMENT.name}>
                    <ProFormaDevelopment
                        navigateToSection={navigateToSection}
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        signUpMode={signUpMode}
                    />
                </RenderIf>
                <RenderIf isTrue={currentTab === PRO_FORMA_TABS.OPERATIONS.name}>
                    <ProFormaOperations hasWriteAccessToProForma={hasWriteAccessToProForma} signUpMode={signUpMode} />
                </RenderIf>
                <RenderIf isTrue={currentTab === PRO_FORMA_TABS.DISPOSITION.name}>
                    <ProFormaDisposition
                        hasWriteAccessToProForma={hasWriteAccessToProForma}
                        signUpMode={signUpMode}
                        proFormaSignUpData={proFormaSignUpData}
                    />
                </RenderIf>
                {signUpMode
                    && currentTab !== PRO_FORMA_TABS.GENERAL_INFORMATION.name && signUpManager
                    && (
                        <SignupOverlay
                            displayManualSignUp={displayManualSignUp!}
                            setDisplayManualSignUp={setDisplayManualSignUp!}
                            signUpManager={signUpManager}
                        />
                    )}
            </NSCard>
        </>
    );
};

export default ProForma;
